import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { ShieldCheck, Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { adminLogin, isAdminLoggedIn } = useApp();
  const { t } = useLanguage();
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isAdminLoggedIn) {
      navigate('/admin');
    }
  }, [isAdminLoggedIn, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const success = await adminLogin(password);
    
    if (success) {
      toast.success(t('adminLoginSuccess'));
      navigate('/admin');
    } else {
      setError(t('incorrectPassword'));
      toast.error(t('accessDenied'));
    }
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 vault-bg">
      <div className="w-full max-w-md animate-slide-up">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png" alt="BMPay" className="h-16 w-16 mb-4" />
          <h1 className="text-2xl font-bold text-[#0A2540] font-['Manrope']" data-testid="admin-title">
            {t('adminBMPay')}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">{t('configPanel')}</p>
        </div>

        <Card className="secure-card-navy rounded-xl overflow-hidden">
          <CardHeader className="bg-gradient-to-b from-slate-50 to-white pb-6 pt-8">
            <h2 className="text-lg font-semibold text-center text-[#0A2540]">
              {t('adminAccessOnly')}
            </h2>
          </CardHeader>

          <CardContent className="px-6 sm:px-8 pb-6">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="password" className="form-label flex items-center gap-2">
                  <Lock className="h-4 w-4" />
                  {t('password')}
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={t('enterAdminPassword')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-14 text-lg border-slate-300 rounded-lg"
                  data-testid="admin-password-input"
                  required
                />
              </div>

              {error && (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg" data-testid="admin-error">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-14 bg-[#0A2540] hover:bg-[#163454] text-white font-semibold text-base rounded-lg"
                disabled={isLoading || !password}
                data-testid="admin-login-button"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner border-white border-t-transparent"></span>
                    {t('verifying')}
                  </span>
                ) : (
                  t('login')
                )}
              </Button>
            </form>
          </CardContent>

          <CardFooter className="flex flex-col items-center py-5 bg-slate-50 border-t border-slate-100">
            <div className="security-badge">
              <ShieldCheck className="h-4 w-4" />
              <span>{t('secureAccess')}</span>
            </div>
          </CardFooter>
        </Card>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-slate-600 hover:text-[#0A2540] hover:underline transition-colors"
            data-testid="back-to-main"
          >
            {t('returnHome')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
